import type { MouseEvent } from 'react';
import type { Subscription } from '../../../../../types/subscription';

import * as styles from './index.css';
import { add, format } from 'date-fns';
import Alert from '@blueshift-ui/feedback/dist/components/alert';
import AlertTitle from '@mui/material/AlertTitle';
import { CheckCircle as CheckCircleIcon } from '@phosphor-icons/react';
import Collapse from '@mui/material/Collapse';
import DATE_FORMATTING_STRINGS from '../../../../../constants/date-formatting-strings';
import RichTranslation from '@blueshift-ui/i18n/dist/components/rich-translation';
import SUB_STATUSES from '../../../../../constants/sub-statuses';
import { useElementClickedHandler } from '@blueshift-ui/analytics';
import { useState } from 'react';
import useTranslation from '@blueshift-ui/i18n/dist/hooks/use-translation';

const TRACKED_CLICK_PROPERTIES = {
  element_id: 'close_membership_updated_banner_button',
};

interface MembershipUpdatedBannerProps {
  subStatus: (typeof SUB_STATUSES)[keyof typeof SUB_STATUSES];
  subscription: Subscription;
}

function MembershipUpdatedBanner({
  subStatus,
  subscription: { billing_period_start_date, billing_period_end_date, name },
}: Readonly<MembershipUpdatedBannerProps>) {
  const { loading: loadingRichTranslate, translate: richTranslate } = useTranslation(
    'membership.updated.banner',
    { rich: true }
  );
  const { loading: loadingTranslate, translate } = useTranslation('membership.updated.banner');

  const [showAlert, setShowAlert] = useState(true);

  const { beginDate, endDate, status } = getSubStatusDetails();

  const trackCloseClick = useElementClickedHandler({
    trackedProperties: TRACKED_CLICK_PROPERTIES,
  });

  function getSubStatusDetails() {
    let status;
    let beginDate;
    let endDate;

    if (subStatus === SUB_STATUSES.DEACTIVATION_PENDING_BILLING_CYCLE) {
      beginDate = billing_period_start_date;
      endDate = billing_period_end_date;
      status = 'cancel';
    } else if (subStatus === SUB_STATUSES.TIER_CHANGE_PENDING) {
      beginDate = billing_period_start_date;
      endDate = billing_period_end_date;
      status = 'updated';
    } else if (subStatus === SUB_STATUSES.FREE_MONTH_CONCESSION_PENDING) {
      beginDate = billing_period_end_date;
      endDate = add(new Date(billing_period_end_date), { months: 1 });
      status = 'free';
    }

    return {
      beginDate,
      endDate,
      status,
    };
  }

  function handleClose(event: React.SyntheticEvent) {
    setShowAlert(false);
    trackCloseClick(event as MouseEvent<HTMLButtonElement>);
  }

  if (loadingRichTranslate || loadingTranslate || !status || !beginDate || !endDate) {
    return null;
  }

  return (
    <Collapse in={showAlert}>
      <Alert
        className={styles.alert}
        icon={<CheckCircleIcon weight="duotone" />}
        onClose={handleClose}
        severity="success"
        variant="filled"
      >
        <AlertTitle>{translate(`${status}_title`, { plan: name })}</AlertTitle>

        <RichTranslation
          i18nKey={`${status}_content`}
          replace={{
            beginDate: format(new Date(beginDate), DATE_FORMATTING_STRINGS.dayOfYear),
            endDate: format(new Date(endDate), DATE_FORMATTING_STRINGS.dayOfYear),
          }}
          translator={richTranslate}
        />
      </Alert>
    </Collapse>
  );
}

export default MembershipUpdatedBanner;
